/**
 * Query params of the Forecast table-data request.
 */
import { ForecastFilters } from './forecastGridTypes';

type Params = Record<string, string | number | boolean>;

interface ForecastParamsInput {
  page: number;
  pageSize: number;
  isSupplier: boolean;
  viewLevel: string;
  /** CR-5286: the top-bar picker (group / company / site) scopes the grid. */
  viewParams: Record<string, number | undefined>;
  appliedFilters: ForecastFilters;
  showStockStatus: boolean;
}

/**
 * Builds the table-data params from the applied filters and view context.
 *
 * @param input - page, side, view context and applied filters
 * @returns the request params (empty filters are omitted)
 */
export function buildForecastTableParams(input: ForecastParamsInput): Params {
  const { page, pageSize, isSupplier, viewLevel, viewParams, appliedFilters: f, showStockStatus } = input;
  const params: Params = {
    page,
    page_size: pageSize,
    company_type: isSupplier ? 'supplier' : 'customer',
    view_level: viewLevel,
  };
  if (viewParams['group'] !== undefined) params['group'] = viewParams['group'];
  if (viewParams['company'] !== undefined) params['company'] = viewParams['company'];
  if (viewParams['site'] !== undefined) params['site'] = viewParams['site'];

  const text: Array<[keyof ForecastFilters, string]> = [
    ['supplierPartNumber', 'supplier_part_number'],
    ['partNumber', 'part_number'],
    ['poNumber', 'po_search'],
    // Quick box: one term, matched against part number, supplier part no. or PO.
    ['partSearch', 'part_search'],
    ['commodityGroup', 'commodity_group'],
    // CR-3855
    ['commodityType', 'commodity_type'],
    ['supplierGroup', 'supplier_group'],
    ['importDateFrom', 'import_date_from'],
    ['importDateTo', 'import_date_to'],
    // CR-6448 supplier panel: single import date + warehouse code
    ['importDate', 'import_date'],
    ['warehouse', 'warehouse_code'],
    // CR-6440 rework: customer panel's Supplier Code (exact match)
    ['supplierCode', 'supplier_code'],
    // CR-6440 cascade filters
    ['companyCountry', 'company_country'],
    ['supplierCountry', 'supplier_country'],
  ];
  text.forEach(([key, param]) => {
    if (f[key]) params[param] = f[key];
  });
  const ids: Array<[keyof ForecastFilters, string]> = [
    ['supplierId', 'supplier_id'],
    ['customerId', 'customer_id'],
    ['recordType', 'record_type_filter'],
    ['division', 'division'],
    ['site', 'site'],
  ];
  ids.forEach(([key, param]) => {
    if (f[key]) params[param] = parseInt(f[key]);
  });

  if (showStockStatus) params['include_stock_status'] = true;
  return params;
}
